import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { api } from "helpers/axios";
import Link from "components/ui/Link";
import plusSlashMinusIcon from "assets/images/icons/plus-slash-minus-icon.png";
import * as Sentry from "@sentry/react";
import { errorMessage } from "helpers/common";
import ErrorAlert from "components/ui/ErrorAlert";
import { Button, Modal } from "components/ui";
import { useTranslation } from "translation/useTranslation";
import { fetchBalanceScorecardList } from "store/balanceScorecardSlice";

function SettingCard({ setting, handleDelete }) {
  const { editableTranslate } = useTranslation();

  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleShowConfirm = (e) => {
    e.preventDefault();

    if (!loading) {
      setLoading(false);
      setShowConfirm(true);
    }
  };

  const handleCancel = (e) => {
    e.preventDefault();
    setShowConfirm(false);
  };

  const handleClickDelete = async (e) => {
    e.preventDefault();
    setLoading(true);
    await handleDelete(setting.hash_id);
    setLoading(false);
    setShowConfirm(false);
  };

  return <div className="col-xxl-4 col-6 mb-4" data-testid={`scorecard-card-${setting.hash_id}`}>
    <div className="peb-summary-card">
      <div className="card-setting-header d-flex justify-content-between">
        <span className="max-50">{setting.name}</span>
        <div className="max-50 text-right">
          <Button variant="ghost" size="inline" tone="danger" icon="bi-trash" onClick={handleShowConfirm} data-testid="delete-button">{editableTranslate('global.delete')}</Button>
          <Button data-testid="edit-button" as={Link} variant="ghost" size="inline" icon="bi-pencil-square" to={`/settings/balance-scorecard/form/${setting.hash_id}`}>{editableTranslate('global.edit')}</Button>
        </div>
      </div>
      <div className="card-setting-body mt-3">
        <div className="row">
          <div className="col-6 card-body-key">{editableTranslate('balance_scorecard.how_to_appraisal')}</div>
          <div className="col-6 card-body-value">{editableTranslate('balance_scorecard.how_to_appraisal.' + setting.settings.howToAppraisal)}</div>

          {
            (setting.settings.howToAppraisal === 'individual_and_systematic_objective' && setting.settings.hasDifferentEvaluationLevel === false) && <>
              <div className="col-6 card-body-key">{editableTranslate('balance_scorecard.number_of_objective_level')}</div>
              <div className="col-6 card-body-value">{setting.settings?.generalPerformanceLevelGrid?.length}</div>
            </>
          }

          {
            (setting.settings.howToAppraisal === 'individual_objective_only' || (setting.settings.howToAppraisal === 'individual_and_systematic_objective' && setting.settings.hasDifferentEvaluationLevel)) && <>
              <div className="col-6 card-body-key">{editableTranslate('balance_scorecard.number_of_individual_objective_level')}</div>
              <div className="col-6 card-body-value">{setting.settings?.individualObjectivePerformanceLevelGrid?.length}</div>
            </>
          }

          {
            (setting.settings.howToAppraisal === 'systematic_objective_only' || (setting.settings.howToAppraisal === 'individual_and_systematic_objective' && setting.settings.hasDifferentEvaluationLevel)) && <>
              <div className="col-6 card-body-key">{editableTranslate('balance_scorecard.number_of_systematic_objective_level')}</div>
              <div className="col-6 card-body-value">{setting.settings?.systematicObjectivePerformanceLevelGrid?.length}</div>
            </>
          }

          <div className="col-6 card-body-key">{editableTranslate('balance_scorecard.performance_result_calculation')}</div>
          <div className="col-6 card-body-value">{editableTranslate('balance_scorecard.performance_result_calculation.' + setting.settings.performanceResultCalculation)}</div>

          <div className="col-6 card-body-key">{editableTranslate('balance_scorecard.allow_changes_of_result')}</div>
          <div className="col-6 card-body-value">{setting.settings.allowChangesOfResult ? editableTranslate('form.yes') : editableTranslate('form.no')}</div>
        </div>
      </div>
    </div>
    <Modal
      open={showConfirm}
      danger
      loading={loading}
      title={editableTranslate('balance_scorecard.delete_confirmation_title', 'Leistungskarteneinstellung löschen?')}
      cancelLabel={editableTranslate('form.no')}
      okLabel={editableTranslate('form.delete.yes')}
      onClose={handleCancel}
      onConfirm={handleClickDelete}
      testIdPrefix="balance-scorecard-delete-"
    >
      <p>{editableTranslate('balance_scorecard.delete_confirmation')}</p>
      {setting?.has_team_objectives && <p className="mb-0">{editableTranslate('balance_scorecard.delete_team_objectives_warning', 'Alle mit dieser Leistungskarteneinstellung verknüpften Teamziele werden ebenfalls gelöscht.')}</p>}
    </Modal>
  </div>
}

export default function BalanceScorecard() {
  const dispatch = useDispatch();

  const { editableTranslate } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [settings, setSettings] = useState([]);

  const fetchSettings = async () => {
    setError(null);
    setLoading(true);
    try {
      const { data, status } = await api.get(`/settings/balanced-scorecards?showSetting=true`);
      if (status === 200) {
        setSettings(data.data);
        setLoading(false);
      }
    } catch (err) {
      setLoading(false);
      setError(errorMessage(err, 'fetch balanced scorecard settings'));
      Sentry.captureException(err);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleDelete = async (hashId) => {
    setError(null);
    try {
      const { status } = await api.delete(`/settings/balanced-scorecards/${hashId}`);
      if (status === 200) {
        dispatch(fetchBalanceScorecardList());

        fetchSettings();
      }
    } catch (err) {
      setError(errorMessage(err, 'delete balanced scorecard setting'));
      Sentry.captureException(err);
    }
  };

  return (
    <>
      <div className="col-lg col-md-9 section-col-right d-flex">
        <div className={`bg-white w-100 p-4 content-card formio-form`}>
          <div className="d-flex justify-content-between">
            <h4 className="mb-0">{editableTranslate("global.menu.settings.balance_scorecard")}</h4>
            <Button data-testid="add-button" as={Link} variant="secondary" to="/settings/balance-scorecard/form">
              <i className="bi bi-plus"></i> {editableTranslate('balance_scorecard.add_setting')}
            </Button>
          </div>

          {
            loading
              ? <div className="text-center" data-testid="loading">
                <div className="spinner-border" role="status">
                  <span className="sr-only">Wird geladen...</span>
                </div>
              </div>
              : <>
                <div className="mt-3">
                  {
                    error && <div>
                      <ErrorAlert error={error} onClose={() => setError(null)} />
                    </div>
                  }

                  <div className="row row-eq-height">
                    {
                      settings.map((setting, i) => {
                        return <SettingCard key={i} setting={setting} handleDelete={handleDelete} />
                      })
                    }
                  </div>
                  {
                    settings.length === 0 && <div className="balancedscorecard-setting-empty">
                      <img src={plusSlashMinusIcon} alt="Empty Balanced Scorecard" />
                      <p>{editableTranslate('balance_scorecard.no_setting')} <span>'{editableTranslate('balance_scorecard.add_setting')}'</span> {editableTranslate('form.button')}.</p>
                    </div>
                  }
                </div>
              </>
          }
        </div>
      </div>
    </>
  );
}
